/**
 * /characters — catalogue of UGC character profiles (issue #589).
 *
 * Workspace-level personas reused across campaigns. Each card shows the
 * reference still, name, and whether a Higgsfield reference id is set
 * (the signal that the persona is wired up for clip-to-clip
 * consistency). Click through to edit; create via the New character
 * button.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, apiJson } from '../lib/api';
import type { Character } from '../lib/types';

export function Characters() {
  const [characters, setCharacters] = useState<Character[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiJson<{ characters: Character[] }>('/api/marketing/characters?limit=200');
      setCharacters(res.characters);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load characters');
      setCharacters([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onDeleted = useCallback((id: string) => {
    setCharacters((cs) => (cs ? cs.filter((c) => c.id !== id) : cs));
  }, []);

  return (
    <div className="marketing-page">
      <header className="marketing-header">
        <div>
          <p className="eyebrow">Characters</p>
          <h1>UGC characters</h1>
          <p>
            Reusable personas for your UGC reels. Attach a Higgsfield reference id so the same
            character stays consistent across clips, then pick a character when generating a brief.
          </p>
        </div>
        <Link to="/characters/new" className="btn btn-primary">
          New character
        </Link>
      </header>

      {error && (
        <div className="alert alert-error" style={{ marginTop: 16 }}>
          {error}
        </div>
      )}

      {characters === null && (
        <div className="card" style={{ marginTop: 24, padding: 24 }}>
          <div className="spinner" />
        </div>
      )}

      {characters && characters.length === 0 && !error && (
        <div
          className="card"
          style={{
            marginTop: 24,
            padding: 32,
            textAlign: 'center',
            color: 'var(--color-text-muted)',
          }}
        >
          <p style={{ marginBottom: 16 }}>No characters yet.</p>
          <Link to="/characters/new" className="btn btn-primary">
            Create your first character
          </Link>
        </div>
      )}

      {characters && characters.length > 0 && (
        <div
          style={{
            marginTop: 24,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 16,
          }}
        >
          {characters.map((c) => (
            <CharacterCard key={c.id} character={c} onDeleted={onDeleted} />
          ))}
        </div>
      )}
    </div>
  );
}

function CharacterCard({
  character,
  onDeleted,
}: {
  character: Character;
  onDeleted: (id: string) => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasHiggsfield = !!character.higgsfield_character_id;

  async function handleDelete(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const ok = window.confirm(
      `Delete "${character.name}"?\n\nRemoves the character profile and its reference image. ` +
        'Jobs generated for this character keep their renders but lose the attribution. Cannot be undone.',
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/marketing/characters/${character.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${body || 'delete_failed'}`);
      }
      onDeleted(character.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
      setDeleting(false);
    }
  }

  return (
    <Link
      to={`/characters/${character.id}`}
      className="card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        textDecoration: 'none',
        color: 'inherit',
        opacity: deleting ? 0.5 : 1,
        pointerEvents: deleting ? 'none' : 'auto',
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={handleDelete}
        disabled={deleting}
        title="Delete character"
        aria-label={`Delete character ${character.name}`}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          width: 24,
          height: 24,
          borderRadius: 6,
          border: 'none',
          background: 'rgba(0,0,0,0.35)',
          color: '#fff',
          cursor: 'pointer',
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1,
        }}
      >
        {deleting ? '⋯' : '✕'}
      </button>

      {character.reference_image_url ? (
        <img
          src={character.reference_image_url}
          alt={character.name}
          style={{
            width: '100%',
            aspectRatio: '1 / 1',
            objectFit: 'cover',
            borderRadius: 8,
            background: '#000',
          }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            aspectRatio: '1 / 1',
            background: 'var(--color-surface-alt)',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-muted)',
            fontSize: 12,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          No image
        </div>
      )}

      <strong style={{ fontSize: 15, lineHeight: 1.2 }}>{character.name}</strong>
      {character.description && (
        <p
          style={{
            margin: 0,
            fontSize: 12,
            color: 'var(--color-text-muted)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {character.description}
        </p>
      )}
      {error && (
        <div className="alert alert-error" style={{ fontSize: 11 }}>
          {error}
        </div>
      )}
      <span
        style={{
          marginTop: 'auto',
          fontSize: 11,
          padding: '2px 6px',
          borderRadius: 4,
          alignSelf: 'flex-start',
          background: hasHiggsfield ? 'rgba(52, 194, 143, 0.15)' : 'var(--color-surface-alt)',
          color: hasHiggsfield ? '#34C28F' : 'var(--color-text-muted)',
        }}
        title={
          hasHiggsfield
            ? `Higgsfield id: ${character.higgsfield_character_id}`
            : 'No Higgsfield reference id set yet'
        }
      >
        {hasHiggsfield ? 'Higgsfield ✓' : 'No Higgsfield id'}
      </span>
    </Link>
  );
}
